import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Menu, X, ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_decor-and-style/artifacts/89wv37zw_LOGO.png";

const CATEGORIES = [
  { label: 'Fashion', value: 'fashion' },
  { label: 'Lifestyle', value: 'lifestyle' },
  { label: 'Home Decor', value: 'home-decor' },
  { label: 'Other', value: 'other' },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <nav data-testid="navbar" className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-xl border-b border-[#E5E7EB]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3" data-testid="navbar-logo">
          <img src={LOGO_URL} alt="Style and Space" className="w-10 h-10 rounded-full object-cover" />
          <span className="font-['Playfair_Display'] text-lg md:text-xl font-bold text-[#0A1128] tracking-tight">
            Style & Space
          </span>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8">
          <DropdownMenu>
            <DropdownMenuTrigger className="flex items-center gap-1 text-sm uppercase tracking-[0.15em] font-medium text-[#0A1128] hover:text-[#6B7280] transition-colors" data-testid="categories-dropdown">
              Categories <ChevronDown className="w-3.5 h-3.5" />
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              {CATEGORIES.map(cat => (
                <DropdownMenuItem key={cat.value} onClick={() => navigate(`/articles/${cat.value}`)} data-testid={`category-${cat.value}`}>
                  {cat.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <a
            href="https://in.pinterest.com/styleandspace4u/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm uppercase tracking-[0.15em] font-medium text-[#0A1128] hover:text-[#6B7280] transition-colors"
            data-testid="pinterest-link"
          >
            Pinterest
          </a>

          {user?.is_admin && (
            <Link to="/admin" className="text-sm uppercase tracking-[0.15em] font-medium text-[#0A1128] hover:text-[#6B7280] transition-colors" data-testid="admin-link">
              Admin
            </Link>
          )}

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-2" data-testid="user-menu">
                <img src={user.picture || '/placeholder.png'} alt={user.name} className="w-8 h-8 rounded-full object-cover border border-[#E5E7EB]" />
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem className="font-medium">{user.name}</DropdownMenuItem>
                {user.is_admin && (
                  <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="admin-menu-item">Admin Panel</DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">Sign Out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link
              to="/login"
              className="bg-[#0A1128] text-white px-6 py-2 text-sm uppercase tracking-widest font-medium hover:bg-[#0A1128]/90 transition-all"
              data-testid="login-btn"
            >
              Sign In
            </Link>
          )}
        </div>

        {/* Mobile Toggle */}
        <button
          className="md:hidden text-[#0A1128]"
          onClick={() => setMobileOpen(!mobileOpen)}
          data-testid="mobile-menu-toggle"
        >
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-[#E5E7EB] px-6 py-6 space-y-4 animate-fade-in" data-testid="mobile-menu">
          {CATEGORIES.map(cat => (
            <Link
              key={cat.value}
              to={`/articles/${cat.value}`}
              className="block text-sm uppercase tracking-[0.15em] font-medium text-[#0A1128]"
              onClick={() => setMobileOpen(false)}
            >
              {cat.label}
            </Link>
          ))}
          <a href="https://in.pinterest.com/styleandspace4u/" target="_blank" rel="noopener noreferrer" className="block text-sm uppercase tracking-[0.15em] font-medium text-[#0A1128]">
            Pinterest
          </a>
          {user?.is_admin && (
            <Link to="/admin" className="block text-sm uppercase tracking-[0.15em] font-medium text-[#0A1128]" onClick={() => setMobileOpen(false)}>
              Admin
            </Link>
          )}
          {user ? (
            <button onClick={handleLogout} className="block text-sm uppercase tracking-[0.15em] font-medium text-[#0A1128]">Sign Out</button>
          ) : (
            <Link to="/login" className="block text-sm uppercase tracking-[0.15em] font-medium text-[#0A1128]" onClick={() => setMobileOpen(false)}>Sign In</Link>
          )}
        </div>
      )}
    </nav>
  );
}
